import {
  ActionButton,
  Modal,
  ModalFooter,
  ModalHeader,
  ModalTitle,
  Padding,
  TextEllipsis,
  Form,
  FormField,
  FormSelect,
  GetApiErrorMessage,
  FormAlerts,
  useMutationErrors,
  useAlertContext,
  GroupDescriptionType,
  SAMPLE_COMMENT_GROUP_VALUE,
  InputDataDetailsKey,
  useQueryParams,
  SampleCommentType,
  FormInput,
  DESCRIPTION_MAX_LENGTH,
  SampleCommentValidFormDataSchema,
  useInputDataDetailsCommentMutation,
  SampleCommentValidFormDataType,
} from '@atlas-frontend/sdk';
import { Flex } from 'antd';
import { FC, useMemo } from 'react';
import translations from './AddCommentModal.translations.json';
import { i18nNsFactory } from '@atlas-frontend/i18n';
import { useInputDataVersions } from '../details/useInputDataVersions';
import { useSubmittedVersion } from '../details/useSubmittedVersion';

const { addTranslations, t } = i18nNsFactory('subject-add-comment-modal');
addTranslations(translations);

const translateMutationError: GetApiErrorMessage = (code, meta) =>
  t(`alert.error.code.${code}`, meta?.args && { ...meta.args });

export interface AddCommentModalProps {
  descriptionId?: number;
  descriptions: GroupDescriptionType[];
  comments: SampleCommentType[];
  sampleId: number;
  groupId?: number;
  onClose: () => void;
  onSubmit: (comment: SampleCommentType) => void;
}

export const AddCommentModal: FC<AddCommentModalProps> = ({
  descriptionId,
  descriptions,
  sampleId,
  onClose,
  onSubmit,
}) => {
  const [{ date, groupId, subject }] = useQueryParams();
  const isGroup = groupId !== undefined;
  const description = descriptions.find((desc) => desc.id === descriptionId);
  const versionData = useInputDataVersions(descriptions);
  const selectedVersion = useSubmittedVersion(description, versionData.version);
  const version = versionData.version;
  const versionId = selectedVersion?.id ?? undefined;
  const { mutateAsync, isPending } = useInputDataDetailsCommentMutation();
  const { mutationErrors, setMutationErrors, clearMutationErrors } =
    useMutationErrors(translateMutationError);
  const { resetAlert, setSuccess } = useAlertContext();

  const keyParams: InputDataDetailsKey = useMemo(
    () => ({
      descriptionId: descriptionId?.toString(),
      groupId,
      valueDate: date ?? '',
      subject: subject ?? '',
      dataStatus: version === 'draft' ? 'draft' : 'active',
      versionId,
    }),
    [subject, descriptionId, groupId, date, version, versionId],
  );

  const groupOrInputOptions = useMemo(() => {
    if (!isGroup) {
      return [
        {
          value: descriptionId,
          label: `${description?.code} - ${description?.name}`,
          disabled: false,
        },
      ];
    }

    return [
      {
        value: SAMPLE_COMMENT_GROUP_VALUE,
        label: t('groupOrInputOptions.group'),
        disabled: false,
      },
      ...(descriptions
        .filter((description) => description.id !== null)
        .map((description) => ({
          label: `${description.code} - ${description.name}`,
          value: description.id as number,
          disabled: description.activeVersionId === null,
        })) ?? []),
    ];
  }, [
    description?.code,
    description?.name,
    descriptionId,
    descriptions,
    isGroup,
  ]);

  const defaultValues: SampleCommentValidFormDataType = useMemo(
    () => ({
      id: null,
      sampleId,
      sampleDataObjectDescriptionId:
        descriptionId || SAMPLE_COMMENT_GROUP_VALUE,
      comments: '',
    }),
    [descriptionId, sampleId],
  );

  return (
    <Form
      schema={SampleCommentValidFormDataSchema}
      defaultValues={defaultValues}
      onSubmit={(newComment: SampleCommentValidFormDataType) => {
        resetAlert();
        clearMutationErrors();
        mutateAsync({
          data: {
            params: keyParams,
            comment: newComment,
          },
        })
          .then((data) => {
            if (data) {
              onSubmit(data);
              setSuccess();
              onClose();
            }
          })
          .catch((error) => {
            setMutationErrors(error?.response?.data?.errors ?? []);
          });
      }}
      serverErrors={mutationErrors}
    >
      <AddCommentModalInner
        groupOrInputOptions={groupOrInputOptions}
        isLoading={isPending}
        onClose={onClose}
      />
    </Form>
  );
};

const AddCommentModalInner: FC<{
  groupOrInputOptions: any[];
  isLoading?: boolean;
  onClose: () => void;
}> = (props) => {
  const { groupOrInputOptions, isLoading, onClose } = props;
  return (
    <Modal
      onCancel={onClose}
      width="35rem"
      footer={
        <ModalFooter>
          <Flex gap="small" justify="end">
            <ActionButton
              type="abort"
              label={t(`button.cancel`)}
              onClick={onClose}
            />
            <ActionButton
              type="formSubmit"
              label={t(`button.add`)}
              loading={isLoading}
            />
          </Flex>
        </ModalFooter>
      }
    >
      <ModalHeader>
        <ModalTitle>
          <TextEllipsis>{t('title')}</TextEllipsis>
        </ModalTitle>
      </ModalHeader>
      <FormAlerts />
      <Padding scrollable>
        <Flex vertical gap="large">
          <FormField
            name="sampleDataObjectDescriptionId"
            label={t('field.sampleDataObjectDescriptionId.label')}
            style={{ maxWidth: '20rem' }}
            required
            preview={{
              enabled: groupOrInputOptions.length <= 1,
              previewOnly: groupOrInputOptions.length <= 1,
              render: (value: any) => {
                const selectedOption = groupOrInputOptions.find(
                  (option) => option.value === value,
                );
                return selectedOption ? selectedOption.label : '';
              },
            }}
          >
            <FormSelect
              name="sampleDataObjectDescriptionId"
              options={groupOrInputOptions}
            />
          </FormField>
          <FormField name="comments" label={t('field.comments.label')} required>
            <FormInput
              name="comments"
              rows={2}
              autoSize
              maxLength={DESCRIPTION_MAX_LENGTH}
            />
          </FormField>
        </Flex>
      </Padding>
    </Modal>
  );
};
